<template>
  <div class="grupos-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-tag-multiple</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Grupos de Produtos</h1>
              <div class="text-caption text-grey">
                Listagem de grupos de produtos cadastrados
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
          
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ filteredGrupos.length }} grupos
              </v-chip>
              
              <v-chip 
                v-if="search"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                1 filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
            class="ml-5"
              color="white"
              variant="flat"
              size="small"
              @click="search = ''"
              :disabled="!search"
            >
              <v-icon size="18" :color="search ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="search ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-text-field
              v-model="search"
              label="Buscar grupo"
              placeholder="Código ou nome do grupo"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              clearable
              hide-details
              density="compact"
            />
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table
        :headers="headers"
        :items="filteredGrupos"
        item-key="codigo_grupo"
        class="scrollable-table"
        density="compact"
        fixed-header
        height="calc(100vh - 380px)"
        :items-per-page="25"
        :items-per-page-options="[10, 25, 50, 100]"
      >
        <!-- Código -->
        <template #item.codigo_grupo="{ item }">
          <v-chip
            color="blue-darken-1"
            variant="tonal"
            size="small"
          >
            {{ item.codigo_grupo }}
          </v-chip>
        </template>

        <!-- Nome -->
        <template #item.nome_grupo_produto="{ item }">
          <div class="text-truncate" style="max-width: 300px">
            {{ item.nome_grupo_produto }}
          </div>
        </template>

        <!-- Status -->
        <template #item.grupo_bloqueado="{ item }">
          <v-chip
            :color="item.grupo_bloqueado === 'N' ? 'success' : 'error'"
            variant="tonal"
            size="small"
          >
            {{ item.grupo_bloqueado === 'N' ? 'Ativo' : 'Bloqueado' }}
          </v-chip>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhum grupo de produto encontrado
          </div>
        </template>
      </v-data-table>
    </v-card>

  </div>
</template>

<script>
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "ConsultaProdutosGrupo",

  data() {
    return {
      grupos: [],
      search: "",
      filtersExpanded: false,

      headers: [
        {
          title: "Código",
          key: "codigo_grupo",
          width: "120px",
        },
        {
          title: "Grupo de Produto",
          key: "nome_grupo_produto",
        },
        {
          title: "Status",
          key: "grupo_bloqueado",
          width: "140px",
        },
      ],
    };
  },

  computed: {
    filteredGrupos() {
      const termo = this.search?.toLowerCase().trim() || "";

      return this.grupos.filter(grupo => {
        if (!termo) return true;

        return (
          String(grupo.codigo_grupo).toLowerCase().includes(termo) ||
          String(grupo.nome_grupo_produto).toLowerCase().includes(termo)
        );
      });
    },
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      try {
        const response = await sapeJodarcService.sapeProdutosGrupo({});

        if (response?.success) {
          this.grupos = Array.isArray(response.data)
            ? response.data
            : [];
        } else {
          this.grupos = [];
          toast.error(response?.message || "Erro ao carregar grupos de produtos");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar grupos de produtos");
        this.grupos = [];
      }
    },

    voltarMenu() {
      this.$router.push({ name: "Menu" });
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
/* ✅ CONTAINER PRINCIPAL */
.grupos-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 16px;
}

/* ✅ ESTILOS DOS FILTROS */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 600;
}

/* ✅ ROTAÇÃO DO ÍCONE EXPANDIR */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

/* ✅ ESTILOS DA TABELA */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ COLUNA CÓDIGO FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 5 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

/* ✅ HOVER DA LINHA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.scrollable-table :deep(.v-data-table__tr:hover .v-data-table__td:first-child) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* ✅ TRUNCATE */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .grupos-container {
    padding: 16px;
  }
  
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    font-size: 0.875rem;
  }
}
</style>
